import {
  type GracePeriodStatus,
  type PaymentFailureNotification,
  type QuotaAdjustmentGracePeriodStatusDto,
} from '@qse/api'
import { create } from 'zustand'

interface BillingStatusState {
  // Current billing status
  billingStatus: 'active' | 'past_due' | 'cancelled' | null
  subscriptionStatus: string | null

  // Grace period information
  gracePeriodStatus: GracePeriodStatus | null

  // Payment failure notifications
  paymentFailureNotifications: PaymentFailureNotification[]

  // Quota adjustment grace period information
  quotaAdjustmentGracePeriodStatus: QuotaAdjustmentGracePeriodStatusDto | null

  // UI state
  showPaymentOverdueBanner: boolean
  showQuotaAdjustmentBanner: boolean
  bannerDismissed: boolean

  // Actions
  setBillingStatus: (status: 'active' | 'past_due' | 'cancelled' | null) => void
  setSubscriptionStatus: (status: string | null) => void
  setGracePeriodStatus: (status: GracePeriodStatus | null) => void
  addPaymentFailureNotification: (notification: PaymentFailureNotification) => void
  removePaymentFailureNotification: (id: string) => void
  clearPaymentFailureNotifications: () => void
  setShowPaymentOverdueBanner: (show: boolean) => void
  setQuotaAdjustmentGracePeriodStatus: (status: QuotaAdjustmentGracePeriodStatusDto | null) => void
  setShowQuotaAdjustmentBanner: (show: boolean) => void
  dismissPaymentOverdueBanner: () => void
  resetBannerDismissal: () => void
  reset: () => void

  // Computed values
  isInGracePeriod: () => boolean
  isPaymentOverdue: () => boolean
  getDaysRemainingInGracePeriod: () => number
  shouldShowPaymentOverdueBanner: () => boolean
}

export const useBillingStatusStore = create<BillingStatusState>((set, get) => ({
  // Initial state
  billingStatus: null,
  subscriptionStatus: null,
  gracePeriodStatus: null,
  paymentFailureNotifications: [],
  quotaAdjustmentGracePeriodStatus: null,
  showPaymentOverdueBanner: false,
  showQuotaAdjustmentBanner: false,
  bannerDismissed: false,

  // Actions
  setBillingStatus: status => set({ billingStatus: status }),

  setSubscriptionStatus: status => set({ subscriptionStatus: status }),

  setGracePeriodStatus: status => set({ gracePeriodStatus: status }),

  addPaymentFailureNotification: notification =>
    set(state => ({
      paymentFailureNotifications: [...state.paymentFailureNotifications, notification],
    })),

  removePaymentFailureNotification: id =>
    set(state => ({
      paymentFailureNotifications: state.paymentFailureNotifications.filter(
        notification => notification.id !== id,
      ),
    })),

  clearPaymentFailureNotifications: () => set({ paymentFailureNotifications: [] }),

  setShowPaymentOverdueBanner: show => set({ showPaymentOverdueBanner: show }),

  setQuotaAdjustmentGracePeriodStatus: status => set({ quotaAdjustmentGracePeriodStatus: status }),

  setShowQuotaAdjustmentBanner: show => set({ showQuotaAdjustmentBanner: show }),

  dismissPaymentOverdueBanner: () => set({ bannerDismissed: true }),

  resetBannerDismissal: () => set({ bannerDismissed: false }),

  // Computed values
  isInGracePeriod: () => {
    const state = get()

    return state.gracePeriodStatus?.isInGracePeriod ?? false
  },

  isPaymentOverdue: () => {
    const state = get()

    return state.billingStatus === 'past_due'
  },

  getDaysRemainingInGracePeriod: () => {
    const state = get()

    if (!state.gracePeriodStatus?.isInGracePeriod) return 0

    const gracePeriodEndString = state.gracePeriodStatus.gracePeriodEnd

    if (!gracePeriodEndString) return 0

    const gracePeriodEnd = new Date(gracePeriodEndString)
    const now = new Date()
    const diffTime = gracePeriodEnd.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return Math.max(0, diffDays)
  },

  shouldShowPaymentOverdueBanner: () => {
    const state = get()

    return state.showPaymentOverdueBanner
  },

  // Reset to initial state
  reset: () =>
    set({
      billingStatus: null,
      subscriptionStatus: null,
      gracePeriodStatus: null,
      paymentFailureNotifications: [],
      quotaAdjustmentGracePeriodStatus: null,
      showPaymentOverdueBanner: false,
      showQuotaAdjustmentBanner: false,
      bannerDismissed: false,
    }),
}))
